// local scope to hold encoders and decoders
const local = {
  encoder: new TextEncoder(),
  decoder: new TextDecoder()
};

/**
 * Handles cryptographic operations for tokens including generating keys,
 * encrypting and decrypting tokens, and exporting/importing keys.
 */
const cryptoHandler = {
  /**
   * Generates a cryptographic key using AES-GCM algorithm.
   * @returns {Promise<CryptoKey>} The promise resolves with a CryptoKey.
   */
  generateKey: function () {
    // Directly return the promise from window.crypto.subtle.generateKey
    return window.crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true, // Key is extractable
      ['encrypt', 'decrypt']
    );
  },

  /**
   * Encrypts a token using AES-GCM.
   * @param {string} token - Token to encrypt.
   * @param {CryptoKey} key - CryptoKey to use for encryption.
   * @returns {Promise<Object>} Encrypted data and IV as an object.
   */
  encryptToken: async function (token, key) {
    // Initialization vector (IV) for encryption
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    // Encrypt token with AES-GCM
    return window.crypto.subtle
      .encrypt(
        { name: 'AES-GCM', iv },
        key,
        local.encoder.encode(token) // Convert token string to Uint8Array
      )
      .then((encrypted) => ({
        encrypted: Array.from(new Uint8Array(encrypted)),
        iv: Array.from(iv),
        key
      }));
  },

  /**
   * Decrypts encrypted data using AES-GCM.
   * @param {ArrayBuffer} encrypted - Encrypted data.
   * @param {CryptoKey} key - CryptoKey to use for decryption.
   * @param {Uint8Array} iv - Initialization vector used for encryption.
   * @returns {Promise<string>} The promise resolves with the decrypted token.
   */
  decryptToken: async function (encrypted, key, iv) {
    // Decrypt token with AES-GCM
    return window.crypto.subtle
      .decrypt(
        { name: 'AES-GCM', iv: new Uint8Array(iv) },
        key,
        new Uint8Array(encrypted)
      ) // Convert decrypted data back to string
      .then((decrypted) => local.decoder.decode(decrypted));
  },

  /**
   * Exports a CryptoKey into a base64 string.
   * @param {CryptoKey} key - CryptoKey to export.
   * @returns {Promise<string>} The promise resolves with a base64 string of the key.
   */
  exportKey: async function (key) {
    return window.crypto.subtle
      .exportKey('raw', key)
      .then((exported) =>
        btoa(String.fromCharCode(...new Uint8Array(exported)))
      );
  },

  /**
   * Imports a CryptoKey from a base64 string.
   * @param {string} exportedKey - Base64 string of the key to import.
   * @returns {Promise<CryptoKey>} The promise resolves with a CryptoKey.
   */
  importKey: function (exportedKey) {
    // Convert base64 string back to ArrayBuffer
    const rawKey = Uint8Array.from(atob(exportedKey), (c) => c.charCodeAt(0));
    return window.crypto.subtle.importKey('raw', rawKey, 'AES-GCM', true, [
      'encrypt',
      'decrypt'
    ]);
  }
};

export default cryptoHandler;
